import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Save, Settings as SettingsIcon, BarChart, Search, Globe, DollarSign } from 'lucide-react';

export default function SettingsPage() {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    site_title: '',
    site_description: '',
    google_analytics_id: '',
    google_search_console_verification: '',
    google_adsense_publisher_id: '',
    google_adsense_auto_ads: false,
    favicon: '',
    logo: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings saved!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-slate-400">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <Toaster position="top-center" theme="dark" />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-slate-400">Configure your site settings</p>
        </div>
        <Button onClick={saveSettings} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* General Settings */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-400" />
              <CardTitle className="text-white">General Settings</CardTitle>
            </div>
            <CardDescription className="text-slate-400">Basic site information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Site Title</label>
              <Input
                value={settings.site_title || ''}
                onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
                placeholder="KayTool"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Site Description</label>
              <Textarea
                value={settings.site_description || ''}
                onChange={(e) => setSettings({ ...settings, site_description: e.target.value })}
                placeholder="Your site description..."
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Logo URL</label>
              <Input
                value={settings.logo || ''}
                onChange={(e) => setSettings({ ...settings, logo: e.target.value })}
                placeholder="https://..."
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Favicon URL</label>
              <Input
                value={settings.favicon || ''}
                onChange={(e) => setSettings({ ...settings, favicon: e.target.value })}
                placeholder="https://..."
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Google Analytics */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-2">
              <BarChart className="w-5 h-5 text-orange-400" />
              <CardTitle className="text-white">Google Analytics</CardTitle>
            </div>
            <CardDescription className="text-slate-400">Track your site visitors</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Google Analytics ID</label>
              <Input
                value={settings.google_analytics_id || ''}
                onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
                placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                Find this in your Google Analytics dashboard under Admin → Property Settings
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Google Search Console */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Search className="w-5 h-5 text-green-400" />
              <CardTitle className="text-white">Google Search Console</CardTitle>
            </div>
            <CardDescription className="text-slate-400">Verify your site with Google</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Verification Code</label>
              <Input
                value={settings.google_search_console_verification || ''}
                onChange={(e) => setSettings({ ...settings, google_search_console_verification: e.target.value })}
                placeholder="google-site-verification=XXXXX"
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter the full meta tag content value from Google Search Console
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Google AdSense */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <CardTitle className="text-white">Google AdSense</CardTitle>
            </div>
            <CardDescription className="text-slate-400">Monetize your site with ads</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">AdSense Publisher ID</label>
              <Input
                value={settings.google_adsense_publisher_id || ''}
                onChange={(e) => setSettings({ ...settings, google_adsense_publisher_id: e.target.value })}
                placeholder="ca-pub-XXXXXXXXXXXXXXXX"
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                Find this in your AdSense dashboard under Account → Account information
              </p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="autoAds"
                checked={settings.google_adsense_auto_ads || false}
                onChange={(e) => setSettings({ ...settings, google_adsense_auto_ads: e.target.checked })}
                className="w-4 h-4 rounded border-slate-600 bg-slate-700"
              />
              <label htmlFor="autoAds" className="text-sm text-slate-300">
                Enable Auto Ads (Google automatically places ads)
              </label>
            </div>
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-xs text-slate-400">
                <strong className="text-slate-300">Manual Ad Placement:</strong> Use the "Ad Block" component in the page builder to place ads exactly where you want them on each page.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* SEO Tools */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-purple-400" />
              <CardTitle className="text-white">SEO Tools</CardTitle>
            </div>
            <CardDescription className="text-slate-400">Access generated SEO files</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href={`${API_URL}/api/settings/sitemap.xml`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
              >
                <p className="font-medium text-white">XML Sitemap</p>
                <p className="text-sm text-slate-400">/api/settings/sitemap.xml</p>
              </a>
              <a
                href={`${API_URL}/api/settings/robots.txt`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
              >
                <p className="font-medium text-white">Robots.txt</p>
                <p className="text-sm text-slate-400">/api/settings/robots.txt</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
